import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  CircularProgress,
  Dialog, IconButton, Slide, Toolbar, Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';

import { ShortStudent } from '../../services/students/types';
import { joinNames } from '../../utils/common/formatText';
import Button from '../Button';
import DataGrid from '../DataGrid';
import DetailsModal, {
  Values,
} from '../DetailsModal';
import SearchField from '../SearchField';
import StudentListItem from '../StudentListItem';

import {
  ButtonContainer,
  SearchContainer, StyledAppBar, StyledBackdrop,
} from './index.styles';

export type Attendee = {
  id: string,
  firstName: string,
  lastName: string,
  note?: string,
}

interface AttendeesDialogProps {
  open: boolean,
  attendees: Attendee[],
  queryResults: ShortStudent[],
  isQueryLoading: boolean,
  isAttendeesLoading?: boolean,
  isLoading?: boolean,
  editable?: boolean,
  handleClose: () => void,
  addAttendee?: (id: string, note?: string) => void,
  removeAttendees?: (attendees: string[]) => void,
  setAttendeeSearch: (search: string) => void,
}

const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) => <Slide direction="up" ref={ref} {...props} />);

const AttendeesDialog = ({
  open,
  attendees,
  queryResults,
  isQueryLoading,
  isAttendeesLoading,
  isLoading,
  editable = true,
  handleClose = () => {},
  addAttendee = () => {},
  removeAttendees = () => {},
  setAttendeeSearch = () => {},
}: AttendeesDialogProps) => {
  const COLUMNS = [
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 200,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 200,
    },
    {
      field: 'note',
      headerName: 'Note',
      width: 600,
    },
  ];

  const [selectedAttendee, setSelectedAttendee] = useState<any>();
  const [selectedRows, setSelectedRows] = useState<any>([]);

  const [addModalOpen, setAddModalOpen] = useState(false);

  const handleDialogClose = () => {
    handleClose();
    setSelectedRows([]);
    setSelectedAttendee(undefined);
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    // Wait for close animation to finish
    setTimeout(
      () => setSelectedAttendee(undefined),
      200,
    );
  };

  return (
    <Dialog
      fullScreen
      open={open}
      TransitionComponent={Transition}
      onClose={handleDialogClose}
    >
      <StyledBackdrop open={isLoading ?? false}>
        <CircularProgress />
      </StyledBackdrop>
      <DetailsModal
        open={addModalOpen}
        handleClose={handleAddModalClose}
        title="Add Attendee"
        fields={{
          attendee: {
            editable: false,
            label: 'Attendee',
            type: 'text',
          },
          note: {
            editable: true,
            label: 'Requested Topic(s)',
            type: 'text',
          },
        }}
        values={{
          attendee: joinNames([
            selectedAttendee?.firstName,
            selectedAttendee?.lastName,
          ]),
        }}
        buttons={[
          {
            fn: () => setAddModalOpen(false),
            label: 'Cancel',
            color: 'error',
            variant: 'text',
          },
          {
            label: 'Add',
            color: 'darkPrimary',
            variant: 'contained',
            fn: (values?: Values, newValues?: Values) => {
              addAttendee(selectedAttendee.id, newValues?.note);
              setAddModalOpen(false);
            },
          },
        ]}
      />
      <StyledAppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleDialogClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h5" component="div">
            Attendees
          </Typography>
        </Toolbar>
      </StyledAppBar>
      {
        editable && (
          <SearchContainer>
            <SearchField
              type="dropdown"
              placeholder="Add Attendee..."
              options={queryResults.filter((x) => !attendees.some((y) => y.id === x.id)) || []}
              getOptionKey={(a) => a?.id}
              getOptionLabel={(a) => joinNames([a?.firstName, a?.lastName])}
              onSearch={(str) => setAttendeeSearch(str)}
              loading={isQueryLoading}
              onSelect={async (a) => {
                if (a) {
                  setSelectedAttendee(a);
                  setAddModalOpen(true);
                }
              }}
              renderOption={(props, s) => (
                <StudentListItem
                  student={s}
                  {...props}
                  key={s.id}
                />
              )}
              clearOnSelect
            />
          </SearchContainer>
        )
      }
      {selectedRows.length > 0 && editable &&
      (
        <ButtonContainer>
          <Button
            onClick={() => removeAttendees(selectedRows)}
            size="medium"
            color="error"
          >
            {`Remove Student${selectedRows.length > 1 ? 's' : ''}`}
          </Button>
        </ButtonContainer>
      )}
      <DataGrid
        rows={attendees}
        columns={COLUMNS}
        pageSizeOptions={[100]}
        checkboxSelection
        getRowId={(row) => row.id}
        autoHeight
        onRowSelectionModelChange={(newSelectionModel) =>
          setSelectedRows(newSelectionModel)}
        rowSelectionModel={selectedRows}
        loading={isAttendeesLoading}
        disableRowSelectionOnClick={false}
      />
    </Dialog>
  );
};

export default AttendeesDialog;
